import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import io from 'socket.io-client';
import {
  Container,
  Box,
  Typography,
  Button,
  Chip,
  CircularProgress,
} from '@mui/material';
import { UserDataContext } from '../../context/UserDataContext';
import BingoCard from './BingoCard';
import CasinoIcon from '@mui/icons-material/Casino';

// Singleton socket instance
let socketInstance = null;

const theme = {
  primary: '#B43361',
  secondary: '#F3D4D4',
  background: '#FFFFFF',
  text: '#000000',
  lightGreen: '#E8F5E9',
};

const Player = () => {
  const { pin } = useParams();
  const navigate = useNavigate();
  const { userData } = useContext(UserDataContext);
  const [game, setGame] = useState({ calledNumbers: [], autoMark: false, gameEnded: false, winner: null });
  const [card, setCard] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentNumber, setCurrentNumber] = useState('');
  const letters = ['C', 'R', 'O', 'W', 'N'];

  useEffect(() => {
    const playerName = sessionStorage.getItem('playerName');
    const playerEmail = sessionStorage.getItem('playerEmail');
    if (!playerName || !playerEmail) {
      navigate('/bingo/join');
      return;
    }

    if (!socketInstance) {
      socketInstance = io('https://bosscrowns-api-a228488a1e46.herokuapp.com', {
        autoConnect: false,
        query: { role: 'player' },
        reconnection: true,
        reconnectionAttempts: 3,
        reconnectionDelay: 1000,
      });
    }

    socketInstance.connect();
    console.log(`Player connected to socket for pin ${pin}, player: ${playerEmail}`);

    socketInstance.emit('join-game', pin);

    const handleGameState = ({ calledNumbers, autoMark, gameEnded, winners }) => {
      console.log(`Received game-state for ${pin}:`, { calledNumbers, autoMark, gameEnded, winners });
      setGame({
        calledNumbers,
        autoMark,
        gameEnded,
        winner: winners && winners.length > 0 ? { name: winners[0].name, email: winners[0].email } : null,
      });
      if (calledNumbers.length > 0) {
        setCurrentNumber(calledNumbers[calledNumbers.length - 1]);
      }
      if (autoMark) {
        console.log(`Auto-mark enabled, marking called numbers for ${pin}`);
        markCalledNumbers(calledNumbers);
      }
    };

    const handleNumberCalled = ({ number, letter, gameEnded }) => {
      console.log(`Number called in ${pin}: ${letter}${number}`);
      const combo = `${letter}${number}`;
      setGame((prev) => {
        if (prev.calledNumbers.includes(combo)) {
          console.warn(`Number ${combo} already in calledNumbers, skipping update`);
          return prev;
        }
        const newCalledNumbers = [...prev.calledNumbers, combo];
        if (prev.autoMark) {
          console.log(`Auto-mark enabled, marking ${combo} for ${pin}`);
          markCalledNumbers(newCalledNumbers);
        }
        return { ...prev, calledNumbers: newCalledNumbers, gameEnded };
      });
      setCurrentNumber(combo);
    };

    const handleAutoMarkUpdated = (autoMark) => {
      console.log(`Received auto-mark-updated for ${pin}: ${autoMark}`);
      setGame((prev) => {
        if (prev.autoMark === autoMark) return prev;
        if (autoMark) {
          console.log(`Auto-mark enabled, marking all called numbers for ${pin}`);
          markCalledNumbers(prev.calledNumbers);
        }
        return { ...prev, autoMark };
      });
    };

    const handlePlayerWon = ({ playerName, playerEmail, gameEnded }) => {
      console.log(`Player won in ${pin}: ${playerName} (${playerEmail})`);
      setGame((prev) => ({
        ...prev,
        gameEnded,
        winner: { name: playerName, email: playerEmail },
      }));
    };

    const handleError = ({ message }) => {
      console.error(`Socket error in ${pin}: ${message}`);
      setIsLoading(false);
      navigate('/bingo/join');
    };

    socketInstance.on('game-state', handleGameState);
    socketInstance.on('number-called', handleNumberCalled);
    socketInstance.on('auto-mark-updated', handleAutoMarkUpdated);
    socketInstance.on('player-won', handlePlayerWon);
    socketInstance.on('error', handleError);

    const fetchPlayerData = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(`https://bosscrowns-api-a228488a1e46.herokuapp.com/bingo/${pin}/join`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: playerName, email: playerEmail }),
        });
        if (!response.ok) throw new Error('Failed to fetch player data');
        const { card } = await response.json();
        setCard(card);
        console.log(`Fetched player card for ${playerEmail} in ${pin}:`, card);
      } catch (err) {
        console.error(`Error fetching player data for ${pin}:`, err);
        navigate('/bingo/join');
      } finally {
        setIsLoading(false);
      }
    };
    fetchPlayerData();

    return () => {
      console.log(`Cleaning up Player socket for ${pin}`);
      if (socketInstance) {
        socketInstance.off('game-state', handleGameState);
        socketInstance.off('number-called', handleNumberCalled);
        socketInstance.off('auto-mark-updated', handleAutoMarkUpdated);
        socketInstance.off('player-won', handlePlayerWon);
        socketInstance.off('error', handleError);
        socketInstance.disconnect();
      }
    };
  }, [pin, navigate]);

  const checkForBingo = useCallback((card) => {
    for (let row = 0; row < 5; row++) {
      if (card[row].every((cell) => cell.marked)) {
        console.log(`Bingo detected in row ${row}`);
        return true;
      }
    }
    for (let col = 0; col < 5; col++) {
      if (card.every((row) => row[col].marked)) {
        console.log(`Bingo detected in column ${col}`);
        return true;
      }
    }
    if (card.every((row, idx) => row[idx].marked)) {
      console.log('Bingo detected in main diagonal');
      return true;
    }
    if (card.every((row, idx) => row[4 - idx].marked)) {
      console.log('Bingo detected in anti-diagonal');
      return true;
    }
    return false;
  }, []);

  const markCalledNumbers = useCallback((calledNumbers) => {
    console.log(`Marking called numbers for ${pin}:`, calledNumbers);
    setCard((prevCard) => {
      const newCard = prevCard.map((row, rowIdx) =>
        row.map((cell, colIdx) => {
          if (!cell.number) return cell;
          const combo = `${letters[colIdx]}${cell.number}`;
          const isMarked = calledNumbers.includes(combo);
          if (isMarked && !cell.marked) {
            console.log(`Marking ${combo} on card at [${rowIdx}, ${colIdx}]`);
          }
          return {
            ...cell,
            marked: isMarked,
          };
        })
      );

      if (checkForBingo(newCard)) {
        const playerName = sessionStorage.getItem('playerName');
        const playerEmail = sessionStorage.getItem('playerEmail');
        console.log(`Bingo detected for ${playerName} (${playerEmail}) in ${pin}`);
        socketInstance.emit('player-won', { pin, playerName, playerEmail });
      }

      return newCard;
    });
  }, [letters, pin, checkForBingo]);

  const markNumber = useCallback(
    (rowIdx, colIdx) => {
      if (game.autoMark) {
        return;
      }
      if (game.gameEnded) {
        return;
      }
      const number = card[rowIdx][colIdx].number;
      if (!number) return;
      const combo = `${letters[colIdx]}${number}`;
      if (!game.calledNumbers.includes(combo)) {
        return;
      }
      if (card[rowIdx][colIdx].marked) {
        return;
      }

      console.log(`Manually marking ${combo} at [${rowIdx}, ${colIdx}]`);
      const newCard = card.map((row, rIdx) =>
        row.map((cell, cIdx) =>
          rIdx === rowIdx && cIdx === colIdx ? { ...cell, marked: true } : cell
        )
      );
      setCard(newCard);

      if (checkForBingo(newCard)) {
        const playerName = sessionStorage.getItem('playerName');
        const playerEmail = sessionStorage.getItem('playerEmail');
        console.log(`Bingo detected for ${playerName} (${playerEmail}) in ${pin}`);
        socketInstance.emit('player-won', { pin, playerName, playerEmail });
      }
    },
    [card, game.autoMark, game.gameEnded, game.calledNumbers, letters, pin, checkForBingo]
  );

  const joinAnotherGame = useCallback(async () => {
    try {
      setIsLoading(true);
      console.log(`Joining another game from ${pin}`);
      navigate('/bingo/join');
    } catch (err) {
      console.error(`Error joining another game from ${pin}:`, err);
    } finally {
      setIsLoading(false);
    }
  }, [navigate, pin]);

  const isCurrentPlayerWinner = game.winner && game.winner.email === sessionStorage.getItem('playerEmail');

  return (
    <Container
      maxWidth="md"
      sx={{
        py: { xs: 3, sm: 5 },
      }}
    >
      <Box
        sx={{
          p: { xs: 2, sm: 4 },
          borderRadius: 2,
          bgcolor: theme.background,
        }}
      >
        <Box display="flex" flexDirection="column" alignItems="center" textAlign="center">
          <CasinoIcon sx={{ fontSize: { xs: 40, sm: 60 }, color: theme.primary, mb: 2 }} />
          <Typography
            variant="h4"
            sx={{
              color: theme.text,
              fontWeight: 'bold',
              mb: { xs: 2, sm: 3 },
              fontSize: { xs: '1.8rem', sm: '2.5rem' },
            }}
          >
            Player Dashboard
          </Typography>
          {game.gameEnded && (
            <>
              <Typography
                variant="h5"
                sx={{
                  color: theme.primary,
                  fontWeight: 'bold',
                  mb: 2,
                  fontSize: { xs: '1.5rem', sm: '2rem' },
                }}
              >
                {isCurrentPlayerWinner ? 'You Won!' : `Game Over! Winner: ${game.winner?.name || 'Unknown'}`}
              </Typography>
              <Button
                variant="contained"
                onClick={joinAnotherGame}
                disabled={isLoading}
                sx={{
                  bgcolor: 'warning.main',
                  '&:hover': { bgcolor: 'warning.dark' },
                  padding: { xs: '8px 16px', sm: '10px 20px' },
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                  mb: 2,
                }}
              >
                Join Another Game
              </Button>
            </>
          )}
          <Typography
            variant="h6"
            sx={{
              color: theme.text,
              mb: 2,
              fontSize: { xs: '1rem', sm: '1.25rem' },
            }}
          >
            Game PIN: {pin}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: theme.text,
              mb: 2,
              fontSize: { xs: '0.9rem', sm: '1rem' },
            }}
          >
            Auto-Mark: {game.autoMark ? 'Enabled' : 'Disabled'}
          </Typography>
          {isLoading && <CircularProgress sx={{ mb: 2 }} />}
          {currentNumber && (
            <Typography
              variant="h6"
              sx={{
                color: theme.primary,
                mb: 2,
                fontSize: { xs: '1rem', sm: '1.25rem' },
              }}
            >
              Current Number: {currentNumber}
            </Typography>
          )}
          <BingoCard
            card={card}
            onMark={markNumber}
            disabled={game.autoMark || game.gameEnded}
            letters={letters}
          />
          <Box sx={{ width: '100%', maxWidth: 600, mt: 3, mb: 3 }}>
            <Typography
              variant="h6"
              sx={{
                color: theme.text,
                mb: 1,
                fontSize: { xs: '1rem', sm: '1.25rem' },
              }}
            >
              Called Numbers
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {game.calledNumbers.map((num, idx) => (
                <Chip
                  key={idx}
                  label={num}
                  sx={{
                    bgcolor: num === currentNumber ? 'warning.main' : theme.primary,
                    color: num === currentNumber ? 'warning.contrastText' : theme.background,
                    fontSize: { xs: '0.8rem', sm: '0.9rem' },
                  }}
                />
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    </Container>
  );
};

export default Player;